import { baseApi } from "./baseApi";
import type { Order, CreateOrderData, OrderStatus } from "../types/order";

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface OrderFilters {
  page?: number;
  limit?: number;
  status?: string;
}

export interface PaginatedOrdersResponse {
  success: boolean;
  data: Order[];
  pagination: PaginationMeta;
  message?: string;
}

/**
 * Orders API slice
 * Handles all order-related API calls
 */
export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create new order
    createOrder: builder.mutation<
      { success: boolean; data: Order; message?: string },
      CreateOrderData
    >({
      query: (orderData) => ({
        url: "/orders",
        method: "POST",
        body: orderData,
        credentials: "include",
      }),
      invalidatesTags: ["Order", "Cart"],
    }),

    // Get order by ID
    getOrderById: builder.query<
      { success: boolean; data: Order; message?: string },
      string
    >({
      query: (id) => ({
        url: `/orders/${id}`,
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "Order", id }],
    }),

    // Get user orders (or all orders for admin) with pagination
    getUserOrders: builder.query<PaginatedOrdersResponse, OrderFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.status) searchParams.append("status", params.status);
        const queryString = searchParams.toString();
        return {
          url: `/orders${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Order"],
    }),

    // Get order by access token (public)
    getOrderByAccessToken: builder.query<
      { success: boolean; data: Order; message?: string },
      string
    >({
      query: (token) => `/orders/public/${token}`,
      providesTags: (_result, _error, token) => [{ type: "Order", id: token }],
    }),

    // Cancel order
    cancelOrder: builder.mutation<
      { success: boolean; data: Order; message?: string },
      string
    >({
      query: (id) => ({
        url: `/orders/${id}/cancel`,
        method: "PUT",
        credentials: "include",
      }),
      invalidatesTags: ["Order"],
    }),

    // Update order status (admin/staff only)
    updateOrderStatus: builder.mutation<
      { success: boolean; data: Order; message?: string },
      { id: string; status: OrderStatus }
    >({
      query: ({ id, status }) => ({
        url: `/orders/${id}/status`,
        method: "PUT",
        body: { status },
        credentials: "include",
      }),
      invalidatesTags: ["Order"],
    }),

    // Lookup order by displayOrderId and email (public - for order tracking)
    lookupOrder: builder.mutation<
      { success: boolean; data: { accessToken: string }; message?: string },
      { displayOrderId: string; email: string }
    >({
      query: ({ displayOrderId, email }) => ({
        url: "/orders/lookup",
        method: "POST",
        body: { displayOrderId, email },
      }),
    }),

    // Create bulk orders
    createBulkOrders: builder.mutation<
      { success: boolean; data: any; message?: string; failed: number },
      any[]
    >({
      query: (orders) => ({
        url: "/orders/bulk",
        method: "POST",
        body: { orders },
        credentials: "include", // Ensure cookies/auth headers are sent
      }),
      invalidatesTags: ["Order"],
    }),

    // Import orders from CSV
    importOrdersCsv: builder.mutation<
      { success: boolean; data: any; message?: string; failed: number },
      FormData
    >({
      query: (formData) => ({
        url: "/orders/import-csv",
        method: "POST",
        body: formData,
        credentials: "include",
      }),
      invalidatesTags: ["Order"],
    }),

    // Update order items (admin/staff only)
    updateOrderItems: builder.mutation<
      { success: boolean; data: Order; message?: string },
      { id: string; items: Array<{ productId: string; quantity: number; variations?: Record<string, string> }> }
    >({
      query: ({ id, items }) => ({
        url: `/orders/${id}/items`,
        method: "PUT",
        body: { items },
        credentials: "include",
      }),
      invalidatesTags: ["Order"],
    }),
  }),
});

export const {
  useCreateOrderMutation,
  useGetOrderByIdQuery,
  useGetUserOrdersQuery,
  useGetOrderByAccessTokenQuery,
  useCancelOrderMutation,
  useUpdateOrderStatusMutation,
  useLookupOrderMutation,
  useCreateBulkOrdersMutation,
  useImportOrdersCsvMutation,
  useUpdateOrderItemsMutation,
} = ordersApi;

